import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CollectionSummary,
  CollectionItemMoveResult,
  MoveCollectionItemsRequest,
  parseMoveCollectionItemsRequest,
} from '@qkindex/contracts';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { authService } from '../../services/authService';
import { createOperationId } from '../../domain/operationId';
import { firebaseCollectionRepository } from '../../repositories/collectionRepository';
import { useSharedCollectionInventory } from '../collection/useSharedCollectionInventory';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { sharedLocations } from './sharingService';

const move = httpsCallable<Record<string, unknown>, CollectionItemMoveResult>(
  functions,
  'moveCollectionItems',
);
export function SharedMoveDialog({
  collectionId,
  shareId,
  linkToken,
  onClose,
  onMoved,
}: {
  collectionId: string;
  shareId: string;
  linkToken?: string;
  onClose(): void;
  onMoved(): void;
}) {
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [target, setTarget] = useState('');
  const [locationId, setLocationId] = useState('');
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [shareLocations, setShareLocations] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const userId = authService.currentUser()?.uid || '';
  const pendingKey = `qkindex.sharedMove:${userId}:${collectionId}:${shareId}:in`;
  const [locked, setLocked] = useState(false);
  const inventory = useSharedCollectionInventory(
    userId,
    target ? [target] : [],
    ['items'],
  );
  const entry = inventory.entries[0];
  const pending = useRef<MoveCollectionItemsRequest | undefined>(undefined);
  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(pendingKey);
      if (!stored) return;
      const request = parseMoveCollectionItemsRequest(JSON.parse(stored));
      if (
        request.destinationShareId !== shareId ||
        request.destinationCollectionId !== collectionId
      )
        return;
      pending.current = request;
      setLocked(true);
      setTarget(request.sourceCollectionId);
      setLocationId(request.destinationLocationId);
      setItemIds(request.itemIds);
      setError(
        'A previous transfer needs its result checked. Check transfer before starting another.',
      );
    } catch {
      window.sessionStorage.removeItem(pendingKey);
    }
  }, [pendingKey, shareId, collectionId]);
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        let cursor: string | undefined;
        const all: CollectionSummary[] = [];
        do {
          const session = await firebaseCollectionRepository.loadSession(
            userId,
            50,
            cursor,
          );
          all.push(...session.availableCollections.items);
          cursor = session.availableCollections.nextCursor;
        } while (cursor && active);
        if (active)
          setCollections(
            all.filter(
              (value) =>
                value.collectionId !== collectionId &&
                value.lifecycle === 'active' &&
                ['owner', 'administrator'].includes(value.role),
            ),
          );
        const result = await sharedLocations({
          collectionId,
          shareId,
          ...(linkToken ? { linkToken } : {}),
        });
        if (active) setShareLocations(result);
      } catch {
        if (active) setError('Unable to load transfer destinations.');
      }
    })();
    return () => {
      active = false;
    };
  }, [collectionId, shareId, linkToken, userId]);
  const locations = shareLocations;
  const submit = async () => {
    if (busy) return;
    const request = pending.current || {
      operationId: createOperationId(),
      sourceCollectionId: target,
      destinationCollectionId: collectionId,
      destinationLocationId: locationId,
      itemIds,
      destinationShareId: shareId,
    };
    pending.current = request;
    try {
      window.sessionStorage.setItem(pendingKey, JSON.stringify(request));
    } catch {
      setError(
        'Transfer could not be saved for recovery. Enable browser session storage and retry.',
      );
      return;
    }
    setLocked(true);
    setBusy(true);
    setError('');
    try {
      const result = (
        await move({
          ...request,
          ...(linkToken ? { destinationLinkToken: linkToken } : {}),
        })
      ).data;
      if (result.state === 'processing')
        setError(
          'Transfer is still processing. Retry checks the same operation.',
        );
      else {
        pending.current = undefined;
        window.sessionStorage.removeItem(pendingKey);
        setLocked(false);
        if (result.entries.some((value) => value.state !== 'moved'))
          setError(
            result.entries
              .map((value) => `${value.sourceItemId}: ${value.state}`)
              .join('; '),
          );
        else onClose();
        onMoved();
      }
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Unable to transfer items. Retry uses the same operation.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <ResponsiveDialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="sm"
    >
      <DialogHeader
        id="shared-move-title"
        title="Transfer items in"
        onClose={onClose}
      />
      <Stack spacing={2} sx={{ p: 3 }}>
        <Typography>
          Move originals from your collection into this share. The shared tag is
          applied automatically.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          select
          disabled={busy || locked}
          label="Source collection"
          value={target}
          onChange={(event) => {
            setTarget(event.target.value);
            setItemIds([]);
            setLocationId('');
          }}
        >
          {collections.map((value) => (
            <MenuItem key={value.collectionId} value={value.collectionId}>
              {value.name}
            </MenuItem>
          ))}
        </TextField>
        {entry?.items.records.map((value) => (
          <FormControlLabel
            key={value.id}
            label={value.keywords || 'Unnamed'}
            control={
              <Checkbox
                checked={itemIds.includes(value.id)}
                disabled={
                  busy ||
                  locked ||
                  (!itemIds.includes(value.id) && itemIds.length >= 20)
                }
                onChange={(_, checked) =>
                  setItemIds((previous) =>
                    checked
                      ? [...previous, value.id]
                      : previous.filter((id) => id !== value.id),
                  )
                }
              />
            }
          />
        ))}
        <TextField
          select
          disabled={busy || locked}
          label="Destination location"
          value={locationId}
          onChange={(event) => setLocationId(event.target.value)}
        >
          {locations.map((value) => (
            <MenuItem key={value.id} value={value.id}>
              {value.name}
            </MenuItem>
          ))}
        </TextField>
        {entry?.status === 'error' ||
        entry?.items.failed ||
        entry?.locations.failed ? (
          <Button onClick={inventory.retry}>Retry loading inventory</Button>
        ) : null}
        <Button
          variant="contained"
          disabled={busy || !target || !locationId || !itemIds.length}
          onClick={() => {
            void submit();
          }}
        >
          {busy ? 'Transferring…' : locked ? 'Check transfer' : 'Transfer'}
        </Button>
      </Stack>
    </ResponsiveDialog>
  );
}
